/**
 * 🎁 **An admin puts an item in their own pack** — the test kit, protocol 47.
 *
 * 🎯 **haugur, 2026-10-01**, after starving to death in the middle of testing
 * hay and losing the kit with everything else he carried: *"which is annoying
 * when one is test alot"* — and he chose **a give command** over a fourth
 * testing override.
 *
 * ## 🔴 Why it goes THROUGH the game, never round it
 *
 * The kit was first written straight into the `inventory` table. **That only
 * works while the player is offline**: a connected player's pack lives in the
 * server's memory and the next save writes over the database. So a give is a
 * message, the server adds the item to the pack it holds, and saves it the way
 * every other item arrives.
 *
 * ## 🔐 Only an admin
 *
 * `users.is_admin` — set by hand, never by the game. **The client only decides
 * whether to show the box**; the server refuses anybody else, because a client
 * can send anything.
 *
 * ## What you type
 *
 * `T`, then a name and an optional count: **`ljár`**, **`ljar 2`**, **`taða 5`**,
 * **`Stone Axe`**. The key, the English name and the Icelandic name all work,
 * and the Icelandic letters are optional — the cookbook search's rule
 * (`thari` finds Þari). A name that is the START of exactly one item is enough.
 */

import { ITEMS } from './items.ts';
import { TUNING } from './tuning.ts';

/**
 * 🔢 **The most one give may hand over: one full stack** — a typo of `5000`
 * must not fill a pack. 🎯 It said 64, Minecraft's stack, and haugur asked for
 * `prik 99` the first evening: **our stacks hold 99** (`tuning.json`).
 */
export const GIVE_MAX = TUNING.inventory.defaultStackSize;

/** 🔤 Lower-case, Icelandic letters spelled out, accents gone — `Þari` → `thari`. */
export function foldName(text: string): string {
  return text
    .toLowerCase()
    .replace(/þ/g, 'th')
    .replace(/ð/g, 'd')
    .replace(/æ/g, 'ae')
    .replace(/ö/g, 'o')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Every name an item answers to, folded. */
function namesOf(item: { key: string; name: string; nameIs: string }): string[] {
  return [foldName(item.key), foldName(item.name), foldName(item.nameIs)];
}

/**
 * 🎯 **Which item a typed name means**, or `undefined` if none or several do.
 *
 * ⚠️ **An exact name wins over a prefix**, so `gras` is grassland and not
 * ambiguous with nothing else; a prefix counts only when exactly ONE item
 * starts with it — `steinox` finds the stone axe, `stein` finds nothing,
 * because a guess between two tools is a wrong item in the pack.
 */
export function itemForName(text: string): string | undefined {
  const want = foldName(text);
  if (want === '') return undefined;
  const exact = ITEMS.filter((i) => namesOf(i).includes(want));
  if (exact.length === 1) return exact[0]?.key;
  if (exact.length > 1) return undefined;
  const prefix = ITEMS.filter((i) => namesOf(i).some((n) => n.startsWith(want)));
  return prefix.length === 1 ? prefix[0]?.key : undefined;
}

/** What a typed line asks for. */
export type GiveRequest =
  | { readonly ok: true; readonly item: string; readonly count: number }
  | { readonly ok: false; readonly reason: 'empty' | 'unknownItem' | 'badCount' };

/**
 * 🎁 **Read a typed line** — `ljár`, `taða 5`, `stone axe 2`.
 *
 * ⚠️ The count is the LAST word, and only when it is a whole number, so a name
 * with spaces in it still works.
 */
export function parseGive(line: string): GiveRequest {
  const words = line.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return { ok: false, reason: 'empty' };
  let count = 1;
  const last = words.at(-1) ?? '';
  if (words.length > 1 && /^\d+$/.test(last)) {
    count = Number(last);
    words.pop();
  }
  if (!Number.isInteger(count) || count < 1 || count > GIVE_MAX) {
    return { ok: false, reason: 'badCount' };
  }
  const item = itemForName(words.join(' '));
  return item === undefined ? { ok: false, reason: 'unknownItem' } : { ok: true, item, count };
}
